import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { StudentNav } from '../../components/shared/BottomNav';
import {
  subscribeToWeekTransactions,
  subscribeToWeeks,
  deleteTransaction,
  updateTransaction,
  formatCurrency,
  getCurrentWeekId,
  subscribeToRecurringTemplates,
  saveRecurringTemplate,
  deleteRecurringTemplate,
  markWeekRecurringApplied,
} from '../../services/firebase';

// Reuse the same EditSheet from StudentDashboard via a local copy
// (keeping files self-contained)

const CATEGORIES = [
  { id: 'food', label: 'Food', emoji: '🍕' },
  { id: 'groceries', label: 'Groceries', emoji: '🛒' },
  { id: 'transport', label: 'Transport', emoji: '🚗' },
  { id: 'entertainment', label: 'Fun', emoji: '🎬' },
  { id: 'clothing', label: 'Clothing', emoji: '👕' },
  { id: 'health', label: 'Health', emoji: '💊' },
  { id: 'books', label: 'Books', emoji: '📚' },
  { id: 'other', label: 'Other', emoji: '📦' },
];

const INCOME_CATEGORIES = [
  { id: 'gift', label: 'Gift', emoji: '🎁' },
  { id: 'job', label: 'Job', emoji: '💼' },
  { id: 'refund', label: 'Refund', emoji: '↩️' },
  { id: 'other', label: 'Other', emoji: '💵' },
];

const ALL_CATEGORIES = [...CATEGORIES, ...INCOME_CATEGORIES];
const FILTER_CATEGORIES = [
  { id: 'all', label: 'All', emoji: '📋' },
  { id: 'planned', label: 'Planned', emoji: '📅' },
  { id: 'income', label: 'Income', emoji: '🎁' },
  ...CATEGORIES,
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const dayLabel = (dayIndex) => {
  const today = new Date().getDay();
  if (dayIndex === today) return 'Today';
  if (dayIndex === (today + 6) % 7) return 'Yesterday';
  return DAYS[dayIndex];
};

const typeColor = (type) => {
  if (type === 'planned') return 'var(--color-accent)';
  if (type === 'income') return 'var(--color-primary)';
  return 'var(--color-error)';
};

const typeLabel = (type) => {
  if (type === 'planned') return '· planned';
  if (type === 'income') return '· income';
  return null;
};

const StudentTransactions = () => {
  const { family, refreshKey } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // includes converted, for lookup
  const [filter, setFilter] = useState('all');
  // Default to last week if closed weeks exist, otherwise current week
  const getLastWeekId = () => {
    const now = new Date();
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7) - 7);
    const startOfYear = new Date(monday.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((monday - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    return `${monday.getFullYear()}-${String(weekNum).padStart(2, '0')}`;
  };

  const [weekFilter, setWeekFilter] = useState(getCurrentWeekId());
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [recurringTemplates, setRecurringTemplates] = useState([]);

  const isCurrentWeek = weekFilter === getCurrentWeekId();
  const selectedWeek = weeks.find(w => w.id === weekFilter);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToWeeks(family.id, w => {
      setWeeks(w);
      // On first load, default to last week if there are closed weeks
      const closedWeeks = w.filter(week => week.closedOut);
      if (closedWeeks.length > 0) {
        setWeekFilter(prev => prev === getCurrentWeekId() ? getLastWeekId() : prev);
      }
    });
    return unsub;
  }, [family?.id, refreshKey]);

  useEffect(() => {
    if (!family?.id) return;
    setLoading(true);
    setExpandedId(null);
    const unsub = subscribeToWeekTransactions(family.id, weekFilter, (txs) => {
      setAllTransactions(txs);
      setTransactions(txs.filter(t => t.type !== 'deleted' && t.type !== 'converted' && t.weekId <= getCurrentWeekId()));
      setLoading(false);
    });
    return unsub;
  }, [family?.id, weekFilter]); // Note: refreshKey intentionally excluded here — Firestore handles reconnection

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToRecurringTemplates(family.id, setRecurringTemplates);
    return unsub;
  }, [family?.id]);

  // Separate effect for refreshKey — re-subscribe without showing loading state
  useEffect(() => {
    if (!family?.id || refreshKey === 0) return;
    const unsub = subscribeToWeekTransactions(family.id, weekFilter, (txs) => {
      setAllTransactions(txs);
      setTransactions(txs.filter(t => t.type !== 'deleted' && t.type !== 'converted' && t.weekId <= getCurrentWeekId()));
    });
    return unsub;
  }, [refreshKey]);

  const handleDelete = async (tx) => {
    await deleteTransaction(family.id, tx.id, transactions);
    setExpandedId(null);
  };

  const handleEdit = async (tx, updates) => {
    await updateTransaction(family.id, tx.id, updates, transactions);
    setEditTx(null);
  };

  // Filter transactions
  const filtered = filter === 'all'
    ? transactions
    : filter === 'planned'
      ? transactions.filter(tx => tx.type === 'planned')
      : filter === 'income'
        ? transactions.filter(tx => tx.type === 'income')
        : transactions.filter(tx => tx.category === filter);

  // Totals
  const spentTotal = transactions
    .filter(t => t.type === 'actual' || !t.type)
    .reduce((s, t) => s + t.amount, 0);
  const plannedTotal = transactions
    .filter(t => t.type === 'planned')
    .reduce((s, t) => s + t.amount, 0);
  const incomeTotal = transactions
    .filter(t => t.type === 'income')
    .reduce((s, t) => s + t.amount, 0);

  // Top category insight (actual spending only)
  const actualTxs = transactions.filter(t => t.type === 'actual' || !t.type);
  const byCategory = actualTxs.reduce((acc, tx) => {
    acc[tx.category] = (acc[tx.category] || 0) + tx.amount;
    return acc;
  }, {});
  const topCategory = Object.entries(byCategory).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="screen">
      <StudentNav />

      <div className="page-header">
        <h1 className="page-title">History</h1>
        {!loading && (
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem',
            fontWeight: 700, color: 'var(--color-error)' }}>
            -{formatCurrency(spentTotal)}
          </span>
        )}
      </div>

      {/* Summary row */}
      {!loading && plannedTotal > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {isCurrentWeek && (
            <div style={{ flex: 1, background: 'var(--color-accent-light)',
              borderRadius: 'var(--radius-md)', padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.8125rem', color: '#C45E1A', fontWeight: 600 }}>
                {formatCurrency(spentTotal)} spent + {formatCurrency(plannedTotal)} more planned this week
              </div>
            </div>
          )}
          {!isCurrentWeek && (
            <div style={{ flex: 1, background: 'var(--color-accent-light)',
              borderRadius: 'var(--radius-md)', padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.7rem', color: '#C45E1A', fontWeight: 600, marginBottom: 2 }}>
                PLANNED
              </div>
              <div style={{ fontWeight: 700, color: '#C45E1A', fontSize: '0.9375rem' }}>
                -{formatCurrency(plannedTotal)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Week selector */}
      <div style={{ overflowX: 'auto', marginBottom: 14, paddingBottom: 4 }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
          {[{ id: getCurrentWeekId(), label: 'This week' },
            ...weeks
              .filter(w => w.id !== getCurrentWeekId() && w.id <= getCurrentWeekId())
              .slice(0, 7)
              .map((w, i) => ({
                id: w.id, label: i === 0 ? 'Last week' : (() => {
                    const start = w.startDate?.toDate ? w.startDate.toDate() : w.startDate ? new Date(w.startDate) : null;
                    if (!start) return w.id;
                    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
                    return `${start.getMonth() + 1}/${start.getDate()}\u2013${end.getMonth() + 1}/${end.getDate()}`;
                  })(),
              }))
          ].map(w => (
            <button key={w.id} onClick={() => setWeekFilter(w.id)}
              style={{
                padding: '8px 16px', borderRadius: 'var(--radius-full)', border: '1.5px solid',
                borderColor: weekFilter === w.id ? 'var(--color-primary)' : 'var(--color-border)',
                background: weekFilter === w.id ? 'var(--color-primary-pale)' : 'white',
                color: weekFilter === w.id ? 'var(--color-primary)' : 'var(--color-text-2)',
                fontWeight: weekFilter === w.id ? 600 : 400,
                fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                whiteSpace: 'nowrap', transition: 'all 150ms ease',
              }}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Closed week summary — saved, match, carryover */}
      {!isCurrentWeek && selectedWeek?.closedOut && (
        <div style={{
          background: 'var(--color-surface-2)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 16,
          display: 'grid',
          gridTemplateColumns: selectedWeek.matchEarned > 0 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
          gap: 10,
          alignItems: 'end',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-3)',
              letterSpacing: '0.05em', marginBottom: 4 }}>SAVED</div>
            <div style={{ fontWeight: 700, color: 'var(--color-primary)', fontSize: '0.9375rem' }}>
              {formatCurrency(selectedWeek.saved || 0)}
            </div>
          </div>
          {selectedWeek.matchEarned > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-3)',
                letterSpacing: '0.05em', marginBottom: 4 }}>MATCH EARNED</div>
              <div style={{ fontWeight: 700, color: 'var(--color-primary)', fontSize: '0.9375rem' }}>
                {formatCurrency(selectedWeek.matchEarned || 0)}
              </div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-3)',
              letterSpacing: '0.05em', marginBottom: 4 }}>ROLLED FORWARD</div>
            <div style={{ fontWeight: 700, color: 'var(--color-text-2)', fontSize: '0.9375rem' }}>
              {formatCurrency(selectedWeek.carryover || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Category/type filter pills */}
      <div style={{ overflowX: 'auto', marginBottom: 20, paddingBottom: 4 }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
          {FILTER_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setFilter(cat.id)}
              style={{
                padding: '6px 14px', borderRadius: 'var(--radius-full)', border: '1.5px solid',
                borderColor: filter === cat.id ? 'var(--color-primary)' : 'var(--color-border)',
                background: filter === cat.id ? 'var(--color-primary)' : 'white',
                color: filter === cat.id ? 'white' : 'var(--color-text-2)',
                fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 150ms ease',
              }}>
              <span>{cat.emoji}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Top category insight */}
      {topCategory && filter === 'all' && actualTxs.length > 2 && (
        <div className="card--accent animate-in" style={{ marginBottom: 20 }}>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>
            💡 Most spent on <strong>{topCategory[0]}</strong> ({formatCurrency(topCategory[1])})
          </p>
        </div>
      )}

      {/* Transaction list */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <div className="spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: 'var(--color-text-3)' }}>
            {filter === 'all' ? 'No transactions this week' : `No ${filter} entries this week`}
          </p>
        </div>
      ) : (
        <div className="stack stack-2 animate-in">
          {filtered.map(tx => {
            const isIncome = tx.type === 'income';
            const isPlanned = tx.type === 'planned';
            const cats = isIncome ? INCOME_CATEGORIES : CATEGORIES;
            const cat = cats.find(c => c.id === tx.category) || cats[cats.length - 1];
            const color = typeColor(tx.type);
            const label = typeLabel(tx.type);
            const txDay = tx.spendDate !== undefined ? tx.spendDate
              : tx.createdAt?.toDate ? tx.createdAt.toDate().getDay()
              : tx.createdAt?.seconds ? new Date(tx.createdAt.seconds * 1000).getDay()
              : new Date().getDay();
            const dayStr = dayLabel(txDay);
            const isExpanded = expandedId === tx.id;
            const canEdit = isCurrentWeek;
            // Find original planned amount if this was converted
            const originalPlanned = tx.convertedFromId
              ? allTransactions.find(t => t.id === tx.convertedFromId)
              : null;

            return (
              <div key={tx.id}>
                <div className="card"
                  onClick={() => canEdit && setExpandedId(isExpanded ? null : tx.id)}
                  style={{ padding: '13px 15px', display: 'flex', alignItems: 'center',
                    gap: 12, borderLeft: `3px solid ${color}`,
                    cursor: canEdit ? 'pointer' : 'default',
                    borderBottomLeftRadius: isExpanded ? 0 : undefined,
                    borderBottomRightRadius: isExpanded ? 0 : undefined,
                    borderBottom: isExpanded ? 'none' : undefined,
                  }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10,
                    background: 'var(--color-surface-2)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                    {cat.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.note || cat.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
                      marginTop: 2, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>{cat.label}</span>
                      {label && (
                        <span style={{ color, fontWeight: 600 }}>{label}</span>
                      )}
                      {originalPlanned && originalPlanned.amount !== tx.amount && (
                        <span style={{ color: 'var(--color-text-3)' }}>
                          · planned {formatCurrency(originalPlanned.amount)}
                          {tx.amount > originalPlanned.amount
                            ? <span style={{ color: 'var(--color-error)' }}> ↑</span>
                            : <span style={{ color: 'var(--color-primary)' }}> ↓</span>
                          }
                        </span>
                      )}
                      <span>· {dayStr}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color }}>
                      {isIncome ? '+' : '-'}{formatCurrency(tx.amount)}
                    </div>
                    {canEdit && (
                      <span style={{ color: 'var(--color-text-3)', fontSize: '0.7rem' }}>
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action bar */}
                {isExpanded && (
                  <div style={{ display: 'flex',
                    border: '1px solid var(--color-border)', borderTop: 'none',
                    borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', overflow: 'hidden' }}>
                    {isPlanned && (
                      <ActionBarBtn label="✓ Spent it"
                        color="var(--color-primary)" bg="var(--color-primary-pale)"
                        onClick={() => {
                          setExpandedId(null);
                          // Navigate back to home to use convert flow
                          window.history.back();
                        }} />
                    )}
                    <ActionBarBtn label="✏️ Edit"
                      color="var(--color-text-2)" bg="var(--color-surface-2)"
                      onClick={() => { setExpandedId(null); setEditTx(tx); }} />
                    <ActionBarBtn label="🗑 Delete"
                      color="var(--color-error)" bg="var(--color-error-light)"
                      onClick={() => handleDelete(tx)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit sheet */}
      {editTx && (
        <EditSheet
          tx={editTx}
          onSave={handleEdit}
          onDelete={handleDelete}
          onClose={() => setEditTx(null)}
          recurringTemplates={recurringTemplates}
          familyId={family?.id}
        />
      )}
    </div>
  );
};

// ─── Action bar button ────────────────────────────────────────────────────────

const ActionBarBtn = ({ label, color, bg, onClick }) => (
  <button onClick={onClick} style={{
    flex: 1, padding: '10px 8px', border: 'none',
    borderRight: '1px solid var(--color-border)',
    background: bg, color, fontWeight: 700, fontSize: '0.8125rem',
    cursor: 'pointer', fontFamily: 'var(--font-body)',
  }}>
    {label}
  </button>
);

// ─── Edit sheet (self-contained copy) ────────────────────────────────────────

const DAYS_ARR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const todayIdx = () => new Date().getDay();

const EditSheet = ({ tx, onSave, onDelete, onClose, recurringTemplates = [], familyId }) => {
  const isIncome = tx.type === 'income';
  const cats = isIncome ? INCOME_CATEGORIES : CATEGORIES;
  const cat = cats.find(c => c.id === tx.category) || cats[cats.length - 1];
  const isPlanned = tx.type === 'planned';

  const matchingTemplate = isPlanned ? recurringTemplates.find(t =>
    t.category === tx.category &&
    t.spendDay === (tx.spendDate !== undefined ? tx.spendDate : todayIdx()) &&
    Math.abs(t.amount - tx.amount) < 0.01
  ) : null;

  const [amount, setAmount] = useState(tx.amount.toString());
  const [category, setCategory] = useState(tx.category);
  const [note, setNote] = useState(tx.note || '');
  const [spendDate, setSpendDate] = useState(tx.spendDate !== undefined ? tx.spendDate : todayIdx());
  const [repeatWeekly, setRepeatWeekly] = useState(!!matchingTemplate);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const today = todayIdx();
  const dayOptions = isPlanned
    ? DAYS_ARR.map((d, i) => ({ index: i, label: i === today ? 'Today' : d }))
    : [0,1,2,3,4,5,6].map(offset => {
        const idx = (today - offset + 7) % 7;
        return { index: idx, label: offset === 0 ? 'Today' : offset === 1 ? 'Yesterday' : DAYS_ARR[idx] };
      });

  const handleSave = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) { setError('Enter a valid amount'); return; }
    setLoading(true);
    try {
      await onSave(tx, { amount: val, category, note, spendDate });
      if (isPlanned && familyId) {
        if (repeatWeekly && !matchingTemplate) {
          await saveRecurringTemplate(familyId, { amount: val, category, note, spendDay: spendDate });
          await markWeekRecurringApplied(familyId, getCurrentWeekId());
        } else if (!repeatWeekly && matchingTemplate) {
          await deleteRecurringTemplate(familyId, matchingTemplate.id);
        } else if (repeatWeekly && matchingTemplate) {
          await deleteRecurringTemplate(familyId, matchingTemplate.id);
          await saveRecurringTemplate(familyId, { amount: val, category, note, spendDay: spendDate });
          await markWeekRecurringApplied(familyId, getCurrentWeekId());
        }
      }
    } catch (err) {
      setError('Failed to save. Try again.');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete(tx);
      onClose();
    } catch (err) {
      setError('Failed to delete. Try again.');
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        <div style={handleStyle} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10,
              background: 'var(--color-surface-2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              {cat.emoji}
            </div>
            <div>
              <h3 style={{ fontSize: '1rem' }}>Edit entry</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                {isPlanned ? 'Planned spend' : isIncome ? 'Income' : 'Actual spend'}
              </div>
            </div>
          </div>
          {!confirmDelete && (
            <button onClick={() => setConfirmDelete(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-error)', fontSize: '0.8125rem', fontWeight: 600,
                fontFamily: 'var(--font-body)' }}>
              🗑 Delete
            </button>
          )}
        </div>

        {confirmDelete && (
          <div className="alert alert--error" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Delete this entry?</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn--secondary" style={{ flex: 1, fontSize: '0.875rem' }}
                onClick={() => setConfirmDelete(false)}>Keep it</button>
              <button className="btn btn--danger" style={{ flex: 1, fontSize: '0.875rem' }}
                onClick={handleDelete} disabled={loading}>
                {loading ? <span className="spinner spinner--white" style={{ width: 16, height: 16 }} /> : 'Yes, delete'}
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSave} className="stack stack-4">
          <div className="form-group">
            <label className="form-label">Amount</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input className="form-input" type="number" min="0.01" step="0.01"
                value={amount} onChange={e => { setAmount(e.target.value); setError(''); }}
                style={{ fontSize: '1.25rem', fontFamily: 'var(--font-display)', fontWeight: 600 }} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{isPlanned ? 'Which day?' : 'When?'}</label>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
              <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                {dayOptions.map(({ index, label }) => (
                  <button key={index} type="button" onClick={() => setSpendDate(index)}
                    style={{
                      padding: '8px 14px', borderRadius: 'var(--radius-full)', border: '1.5px solid',
                      borderColor: spendDate === index ? 'var(--color-primary)' : 'var(--color-border)',
                      background: spendDate === index ? 'var(--color-primary-pale)' : 'white',
                      color: spendDate === index ? 'var(--color-primary)' : 'var(--color-text-2)',
                      fontWeight: spendDate === index ? 700 : 400,
                      fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      whiteSpace: 'nowrap', transition: 'all 150ms ease',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Category</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {cats.map(c => (
                <button key={c.id} type="button" onClick={() => setCategory(c.id)}
                  style={{
                    padding: '10px 4px', borderRadius: 'var(--radius-md)', border: '2px solid',
                    borderColor: category === c.id ? 'var(--color-primary)' : 'var(--color-border)',
                    background: category === c.id ? 'var(--color-primary-pale)' : 'white',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 4, transition: 'all 150ms ease',
                    fontFamily: 'var(--font-body)',
                  }}>
                  <span style={{ fontSize: 20 }}>{c.emoji}</span>
                  <span style={{ fontSize: '0.65rem', fontWeight: 600,
                    color: category === c.id ? 'var(--color-primary)' : 'var(--color-text-3)' }}>
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Note <span style={{ fontWeight: 400, color: 'var(--color-text-3)' }}>(optional)</span>
            </label>
            <input className="form-input" type="text" placeholder="Add a note..."
              value={note} onChange={e => setNote(e.target.value)} maxLength={60} />
          </div>

          {isPlanned && (
            <div className="toggle-wrapper" style={{ padding: '4px 0' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>
                  Repeat weekly
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                  {matchingTemplate && !repeatWeekly
                    ? 'Turn off to remove from recurring items'
                    : 'Add this to your plan every week automatically'}
                </div>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={repeatWeekly}
                  onChange={e => setRepeatWeekly(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
          )}

          {error && <div className="alert alert--error">{error}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn--secondary"
              onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn--primary"
              disabled={loading} style={{ flex: 2 }}>
              {loading
                ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
                : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  zIndex: 200, display: 'flex', alignItems: 'flex-end',
};
const sheetStyle = {
  background: 'var(--color-surface)', borderRadius: '20px 20px 0 0',
  padding: '12px 24px 40px', width: '100%', maxWidth: 'var(--max-width)',
  margin: '0 auto', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
  maxHeight: '90vh', overflowY: 'auto',
};
const handleStyle = {
  width: 36, height: 4, borderRadius: 2, background: 'var(--color-border)',
  margin: '0 auto 20px', display: 'block',
};

export default StudentTransactions;
